// ...existing code...
import React, { useState, useEffect, useRef } from 'react'
import { messageSenderLabel } from '../../utils/safeSpaceMessageSender';
import {Grid, AppBar, Container, Stack, IconButton, Button, RadioGroup, TextField, Popover, Box, Typography, Avatar} from '@mui/material'
import WbButton from '../../components/common/WbButton'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { toPupilViewableArtifactUrl } from '../../utils/staffAvatar';
import MoodOutlinedIcon from '@mui/icons-material/MoodOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CodeIcon from '@mui/icons-material/Code';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { useLocation } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import { safeSpaceApi } from '../../services/api/safespace/safeSpaceApi';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import paths from '../../routes/paths'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { toast } from 'react-toastify';
import unifiedLogout from '../../utils/unifiedLogout';

export default function StartConversation() {
  const navigate = useNavigate();
  const handleGoHome = () => {
    navigate(paths.pupilSurveys);
  };
  const location = useLocation();
  const messageEndRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  // Highest message id currently rendered. A ref rather than state because the poll below reads it
  // from inside an interval closure, where a state value would be frozen at whatever it was when the
  // interval was created and every poll would re-request the same messages.
  const highestMessageIdRef = useRef(0);
  const [messages, setMessages] = useState([]);
  const [conversationDetails, setConversationDetails] = useState(null);
  const [isConversationClosed, setIsConversationClosed] = useState(false);
  const pupilData = JSON.parse(localStorage.getItem('pupil') || '{}');
  const staffData = JSON.parse(localStorage.getItem('selectedStaff') || '{}');
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // File attachment states
  const [selectedFile, setSelectedFile] = useState(null);       // Stores the File object
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);   // URL for preview display
  const [fileName, setFileName] = useState(null);               // Full file name
  const [fileValue, setFileValue] = useState(null);             // Base64 encoded file data
  const [truncatedFileName, setTruncatedFileName] = useState(null); // Shortened filename for UI
  const fileInputRef = useRef(null);                           // Reference to hidden file input

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // File handling functions
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      
      // Process filename for display
      const splitFileName = file.name.split('.');
      const imageName = splitFileName[0];
      const trancateString = 
          imageName.length > 23
          ? imageName.substring(0, 23) + '...' + (splitFileName.length > 1 ? '.' + splitFileName[splitFileName.length - 1] : '')
          : file.name;
      
      setFileName(file.name);
      setTruncatedFileName(trancateString);
      
      // Create a preview URL for the file (for display only)
      const previewReader = new FileReader();
      previewReader.onload = (e) => {
        setFilePreviewUrl(e.target.result);
      };
      previewReader.readAsDataURL(file);
      
      // Read as binary string for data storage
      const reader = new FileReader();
      reader.onload = function(readerEvt) {
        const binaryString = readerEvt.target.result;
        setFileValue(btoa(binaryString)); // Convert binary to base64
      };
      reader.readAsBinaryString(file);
    }
  };

  // Utility function to open file dialog
  const triggerFileInput = () => {
    // Guarded: an unset ref threw inside the click handler, which surfaces only in the console and
    // presents to the user as the attachment icon doing nothing at all.
    fileInputRef.current?.click();
  };

  // Utility function to remove selected file
  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setFileName(null);
    setFileValue(null);
    setTruncatedFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Read from localStorage and get message type 3 (IsAnnonymous)
  const safeSpaceConfig = JSON.parse(localStorage.getItem('safeSpaceConfig'));
  const autoResponceMessage = safeSpaceConfig?.messages?.find((msg) => msg.messageType === 0);
  const anonymousMessage = safeSpaceConfig?.messages?.find((msg) => msg.messageType === 3);
  const anonymousoutOfHoursMessage = safeSpaceConfig?.messages?.find((msg) => msg.messageType === 1);

  const autoResponceMessageContent = autoResponceMessage?.messageContent;
  const isautoResponceEnabled = autoResponceMessage?.isEnabled;

  const anonymousoutOfHoursMessageContent = anonymousoutOfHoursMessage?.messageContent;
  const isoutOfHoursEnabled = anonymousoutOfHoursMessage?.isEnabled;
  const outOfHoursStartTime = anonymousoutOfHoursMessage?.outOfHoursStartTime;
  const outOfHoursEndTime = anonymousoutOfHoursMessage?.outOfHoursEndTime;
  // Helper to check if current time is within out-of-hours
  function isWithinOutOfHours() {
    if (!isoutOfHoursEnabled || !outOfHoursStartTime || !outOfHoursEndTime) return false;
    const now = new Date();
    const [startHour, startMinute] = outOfHoursStartTime.split(':').map(Number);
    const [endHour, endMinute] = outOfHoursEndTime.split(':').map(Number);
    const start = new Date(now);
    start.setHours(startHour, startMinute, 0, 0);
    const end = new Date(now);
    end.setHours(endHour, endMinute, 0, 0);
    if (end < start) {
      // Overnight range
      if (now >= start) return true;
      if (now <= end) return true;
      return false;
    }
    return now >= start && now <= end;
  }

  // `silent` refetches without raising the spinner.
  //
  // Sending a reply already appends the pupil's message to the thread from the send response, so it
  // is on screen immediately. The sync refetch that followed then set loading, which blanked that
  // thread behind a spinner for as long as the round trip took — around two seconds — and a child
  // who had just pressed send watched their own message disappear and come back. Nothing was slow
  // enough to justify hiding what was already correct.
  const fetchMessages = async ({ silent = false, delta = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const conversationId = location.state?.conversationId;

      if (conversationId) {
        const response = await safeSpaceApi.getStaffConversationsMessagesById(
          conversationId,
          pupilData.groupId ? true : false,
          pupilData.groupId || 0,
          pupilData.schoolId || 0,
          delta ? highestMessageIdRef.current : null
        );

        // Nothing new since the last poll — the ordinary case. Leave the thread untouched rather
        // than re-rendering it identically every few seconds.
        if (delta && (!Array.isArray(response) || response.length === 0)) {
          if (!silent) setLoading(false);
          return;
        }

        if (response) {
          const filteredMessages = response.filter((message) => !message.isANote);

          if (delta) {
            // Append, keyed by id. The pupil's own message is already on screen from the send
            // response, so the poll must not render it a second time.
            setMessages((previous) => {
              const seen = new Set(previous.map((m) => m.id));
              const additions = filteredMessages.filter((m) => !seen.has(m.id));
              return additions.length > 0 ? [...previous, ...additions] : previous;
            });
          } else {
            setMessages(filteredMessages);
          }

          // Track the newest id so the next poll asks only for what follows it. Internal notes are
          // filtered out for pupils but still occupy ids, so the mark is taken from the raw
          // response — otherwise a note would be re-requested on every poll forever.
          const highest = response.reduce(
            (max, m) => (m?.id > max ? m.id : max),
            delta ? highestMessageIdRef.current ?? 0 : 0
          );
          if (highest > 0) highestMessageIdRef.current = highest;

          const firstMessage = filteredMessages[0];
          if (!delta && firstMessage) {
            setConversationDetails({
              title: location.state?.title || 'Conversation',
              responderId: firstMessage.responderId,
            });
            // Check if the conversation is closed
            const isConvClosed = location.state?.isConversationClosed;
            setIsConversationClosed(isConvClosed || false);
          }

          try {
            // Marked seen on every load, delta included: a staff reply arriving while the pupil has
            // the thread open has been seen by them.
            const payload = {
              conversationId: location.state?.conversationId,
              studentId: pupilData.studentId,
              schoolId: pupilData.schoolId,
              // Derived, the way line 622 in this same file already does it. Hardcoding true sent a
              // MAT claim with groupId 0 for every pupil at an individual school, which asked the
              // API for dataset "Group0".
              isMAT: pupilData.groupId ? true : false,
              groupId: pupilData.groupId || 0,
            };
            await safeSpaceApi.StudentConversationSeen(payload);
          } catch (error) {
            console.error('Error marking student conversation messages as seen:', error);
          } finally {
            // nothing
          }
        }
      }
      if (!silent) setLoading(false);
      scrollToBottom();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      if (!silent) setLoading(false);
    }
  };

  // Effect to fetch messages when location changes
  // Keyed on the conversation id rather than location.state. The state object is compared by
  // reference, so the router handing back a new object on any re-render re-ran this — refetching
  // the whole thread and re-posting the "seen" call each time. The id is a primitive, so the
  // effect now runs once per conversation.
  useEffect(() => {
    highestMessageIdRef.current = 0;
    fetchMessages();
    // eslint-disable-next-line
  }, [location.state?.conversationId]);

  // Keep the thread current without the pupil reloading the page.
  //
  // The staff dashboard already polls for new messages, so a pupil's message appears to a responder
  // within seconds. The reverse did not happen: a staff reply sat unseen until the child refreshed
  // the browser. A child who has just asked for help and is watching for an answer is the last
  // person who should have to know to press reload.
  //
  // Polling rather than a push transport, deliberately — Custom-Api runs several Cloud Run instances
  // with no shared backplane, so a connection held on one would never learn about a reply posted to
  // another. It asks only for messages newer than the newest one rendered, so the ordinary case is a
  // request that returns an empty array.
  //
  // Paused while the tab is hidden, and catches up on return.
  useEffect(() => {
    const conversationId = location.state?.conversationId;
    if (!conversationId) return undefined;

    const POLL_INTERVAL_MS = 5000;

    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      fetchMessages({ silent: true, delta: true });
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', poll);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
    };
    // eslint-disable-next-line
  }, [location.state?.conversationId]);

  // Effect to scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Helper function for date formatting (converts UTC to local browser datetime)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const utcDate = new Date(dateString);
    if (!(utcDate instanceof Date) || isNaN(utcDate)) return '';
    // Convert to local browser datetime
    const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000 * -1);
    // Format: July 17, 2025 at 12:11 PM
    const datePart = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(localDate);
    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(localDate);
    return `${datePart} at ${timePart}`;
  };
  
  // File utility functions
  
  // Check if a file is an image based on URL or extension
  const isImageFile = (url) => {
    if (!url) return false;
    
    // Image extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    
    // Special handling for ReadImageFile API URLs
    if (url.toLowerCase().includes('/api/artifact/readimagefile')) {
      return true;
    }
    
    // Check if URL contains words like 'image' or common image extensions
    const urlLower = url.toLowerCase();
    if (urlLower.includes('image') || 
        imageExtensions.some(ext => urlLower.includes('.' + ext))) {
      return true;
    }
    
    // Get extension using our helper method
    const extension = getFileExtension(url);
    return imageExtensions.includes(extension.toLowerCase());
  };

  // Extract file extension from URL
  const getFileExtension = (url) => {
    if (!url) return '';
    
    // Special handling for API URLs with fileName parameter
    if (url.includes('/api/artifact/') && url.includes('fileName=')) {
      // Extract the filename from the URL parameter
      const filenameMatch = url.match(/fileName=([^&]+)/);
      if (filenameMatch && filenameMatch[1]) {
        const filenamePart = decodeURIComponent(filenameMatch[1]);
        
        // Check if the extracted part has a file extension
        const lastDotIndex = filenamePart.lastIndexOf('.');
        if (lastDotIndex !== -1) {
          const extension = filenamePart.substring(lastDotIndex + 1).toLowerCase();
          return extension;
        }
      }
    }
    
    // Regular handling for standard URLs
    // Split on '?' to remove query parameters
    const path = url.split('?')[0];
    // Get the filename part after the last slash
    const filename = path.split('/').pop();
    if (!filename || filename.indexOf('.') === -1) {
      // If no extension found in URL path, check if it's a ReadImageFile endpoint and return a default
      if (url.toLowerCase().includes('/readimagefile')) {
        return 'jpg';
      }
      return '';
    }
    // Return the extension part (after the last dot)
    return filename.split('.').pop().toLowerCase();
  };

  // Extract filename from URL
  const getFilenameFromUrl = (url) => {
    if (!url) return 'Attachment';
    
    // Special handling for API URLs with fileName parameter
    if (url.includes('/api/artifact/') && url.includes('fileName=')) {
      const filenameMatch = url.match(/fileName=([^&]+)/);
      if (filenameMatch && filenameMatch[1]) {
        const fullPath = decodeURIComponent(filenameMatch[1]);
        
        // Get the actual filename from the path (after the last slash)
        const actualFilename = fullPath.split('/').pop();
        
        if (actualFilename) return actualFilename;
      }
    } else {
      // Regular handling for standard URLs
      // Split on '?' to remove query parameters
      const path = url.split('?')[0];
      // Get the filename part after the last slash
      const filename = path.split('/').pop();
      
      if (filename) return filename;
    }
    
    // Return "Attachment" + extension if available
    const extension = getFileExtension(url);
    return extension ? `Attachment.${extension}` : 'Attachment';
  };

  // Get shortened filename for display
  const getShortFilename = (filename) => {
    if (!filename) return 'Attachment';
    
    if (filename.length <= 18) return filename;
    
    const parts = filename.split('.');
    const extension = parts.length > 1 ? parts.pop() : '';
    const name = parts.join('.');
    
    return name.substring(0, 15) + '...' + (extension ? '.' + extension : '');
  };
  
  // Helper function to download blob (defined first since it's used by handleDownloadAttachment)
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };
  
  // Handle attachment download
  const handleDownloadAttachment = async (rawAttachmentURL, fileName) => {
    if (!rawAttachmentURL) return;

    // Route through Custom-Api's anonymous artifact proxy before anything touches the network.
    // Admin-Api wants an Authorization header that a pupil's passcode session has no way to supply,
    // so the XHR below, the fetch fallback after it and the <a download> after that all came back
    // 401. Rewriting once here covers all three.
    const attachmentURL = toPupilViewableArtifactUrl(rawAttachmentURL);
    
    // Get file extension for proper naming and MIME type
    let extension = '';
    let downloadFileName = fileName || 'Attachment';
    
    // Special handling for API URLs with fileName parameter
    if (attachmentURL.includes('/api/artifact/') && attachmentURL.includes('fileName=')) {
      const filenameMatch = attachmentURL.match(/fileName=([^&]+)/);
      if (filenameMatch && filenameMatch[1]) {
        const fullPath = decodeURIComponent(filenameMatch[1]);
        const actualFilename = fullPath.split('/').pop();
        
        if (actualFilename) {
          downloadFileName = actualFilename;
          const lastDotIndex = actualFilename.lastIndexOf('.');
          if (lastDotIndex !== -1) {
            extension = actualFilename.substring(lastDotIndex + 1).toLowerCase();
          }
        }
      }
    } else {
      // Regular handling for standard URLs
      const path = attachmentURL.split('?')[0];
      const filename = path.split('/').pop();
      
      if (filename) {
        const lastDotIndex = filename.lastIndexOf('.');
        if (lastDotIndex !== -1) {
          extension = filename.substring(lastDotIndex + 1).toLowerCase();
        }
      }
    }
    
    // Default to 'attachment' if no extension found
    if (!downloadFileName.includes('.') && extension) {
      downloadFileName = `Attachment.${extension}`;
    }
    
    // Try XMLHttpRequest first (sometimes handles CORS better than fetch)
    // XMLHttpRequest can work better with cross-origin requests in some browsers
    try {
      const blob = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', attachmentURL, true);
        xhr.responseType = 'blob';
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error occurred'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('Request timeout'));
        };
        
        // Don't set withCredentials to avoid CORS preflight if not needed
        // Browser will still send cookies for same-origin requests
        xhr.timeout = 30000; // 30 second timeout
        xhr.send();
      });
      
      // Process blob
      let processedBlob = blob;
      
      // Check blob type and fix if needed
      const mimeTypes = {
        'pdf': 'application/pdf',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'txt': 'text/plain'
      };
      
      // If blob type is generic or HTML, fix it based on extension
      if (!blob.type || blob.type === 'text/html' || blob.type === 'application/octet-stream') {
        const correctMimeType = extension && mimeTypes[extension] 
          ? mimeTypes[extension] 
          : blob.type || 'application/octet-stream';
        
        // Recreate blob with correct MIME type
        const reader = new FileReader();
        reader.onloadend = function() {
          const arrayBuffer = reader.result;
          processedBlob = new Blob([arrayBuffer], { type: correctMimeType });
          downloadBlob(processedBlob, downloadFileName);
        };
        reader.onerror = function() {
          // If FileReader fails, try with original blob
          downloadBlob(blob, downloadFileName);
        };
        reader.readAsArrayBuffer(blob);
        return; // Exit early, download will happen in onloadend
      }
      
      // Download the blob
      downloadBlob(processedBlob, downloadFileName);
      
    } catch (error) {
      console.warn('XMLHttpRequest failed (likely CORS), trying fetch:', error);
      
      // Fallback 1: Try fetch without credentials
      try {
        const response = await fetch(attachmentURL, {
          method: 'GET',
          redirect: 'follow'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        let blob = await response.blob();
        
        // Fix MIME type if needed
        const mimeTypes = {
          'pdf': 'application/pdf',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'txt': 'text/plain'
        };
        
        if (!blob.type || blob.type === 'text/html') {
          const correctMimeType = extension && mimeTypes[extension] 
            ? mimeTypes[extension] 
            : 'application/octet-stream';
          const arrayBuffer = await blob.arrayBuffer();
          blob = new Blob([arrayBuffer], { type: correctMimeType });
        }
        
        downloadBlob(blob, downloadFileName);
        
      } catch (fetchError) {
        console.warn('Fetch also failed, using direct link fallback:', fetchError);
        
        // Fallback 2: Direct link (may open in new tab due to CORS)
        const link = document.createElement('a');
        link.href = attachmentURL;
        link.setAttribute('download', downloadFileName);
        link.setAttribute('target', '_blank');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        
        toast.info('Download initiated. If the file opens in a new tab, right-click and select "Save As" to download.');
      }
    }
  };

  const handleSendMessage = async () => {
    if (isConversationClosed) {
      toast.error('This conversation has been closed. You cannot send any more messages.');
      return;
    }
    
    if (!messageText.trim() && !fileValue) {
      toast.error('Please enter a message or attach a file');
      return;
    }

    try {
      setSendingMessage(true);
      const replyData = {
        isMAT: pupilData.groupId ? true : false,
        groupId: pupilData.groupId || 0,
        schoolId: pupilData.schoolId,
        conversationId: location.state?.conversationId,
        staffId: conversationDetails?.responderId,
        studentId: pupilData.studentId,
        messageText: messageText.trim(),
        isInternalNote: false,
        createdDate: new Date().toISOString(),
        // Add file attachment data if there's a file
        Attachment: fileValue ? {
          fileName: fileName,
          fileData: fileValue, // Base64 encoded file data
          fileType: selectedFile?.name.split('.').pop() || ''
        } : null
      };

      const response = await safeSpaceApi.AddConversationsMessagesReplies(replyData);

      if (response) {
        // Show the message straight away from what the send returned.
        //
        // AddSafeSpaceConversationReply now returns the message it created, shaped the way the
        // thread renders one. Before that it returned only an id, and pushing the raw response into
        // state produced a message with no messageText and no studentName — the pupil saw an empty
        // bubble captioned "Message sent by undefined" until a refetch replaced it.
        const sentText = messageText.trim();

        // Older API: build the message locally. Everything needed is here — the text was just typed
        // and the sender is the pupil — and studentName is read off the thread rather than assumed,
        // so the caption matches the ones above it exactly.
        const sent = response?.message ?? {
          id: response?.messageId,
          messageText: sentText,
          studentId: pupilData.studentId,
          studentName: [...messages].reverse().find((m) => m?.studentName)?.studentName || '',
          createdDate: new Date().toISOString(),
        };

        setMessages((prev) => {
          // Keyed by id so the poll that follows cannot render this message a second time.
          if (sent?.id && prev.some((m) => m?.id === sent.id)) return prev;
          return [...prev, sent];
        });

        // Move the cursor past the sent message. The poll asks for everything after it, so leaving
        // it behind means the very next poll re-delivers what was just appended — a pupil seeing
        // their own message twice in a conversation about something difficult.
        if (typeof sent?.id === 'number' && sent.id > highestMessageIdRef.current) {
          highestMessageIdRef.current = sent.id;
        }

        // Clear input field and attachment
        setMessageText('');
        
        // Clear file attachment states
        removeSelectedFile();
        
        // Silent: the message is already rendered above, so this only reconciles against the
        // server. Showing a spinner over it would hide content that is already correct.
        fetchMessages({ silent: true });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <>
      <AppBar className="header" position="static" sx={{ background: '#1976d2', boxShadow: 2 }}>
        <Container maxWidth="xxl">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ minHeight: 64, position: 'relative' }}
          >
            <div className="logo">
              <span>Welbee</span>
            </div>

            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
            <Button
              variant="text"              
              onClick={handleGoHome}
              startIcon={<KeyboardBackspaceIcon />}
              sx={{ color: '#fff', textTransform: 'none' }}
            >
              Back to Home
            </Button>
            <div>
              <nav>
                <WbButton
                  onClick={() => {
                    unifiedLogout(paths.pupilDashLogin);
                  }}
                  CustomButtonText="Logout"
                />
              </nav>
            </div>
            </Stack>
          </Stack>
        </Container>
      </AppBar>
      <div className="main-container" style={{ position: 'relative' }}>
        {/* Floating Out-of-Hours Message */}
        {isoutOfHoursEnabled && isWithinOutOfHours() && (
          <div
            style={{
              position: 'fixed',
              top: 100,
              right: 20,
              zIndex: 9999,
              background: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffeeba',
              borderRadius: 8,
              padding: '16px 24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              minWidth: 300,
              maxWidth: 300,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Out of Hours</div>
            <div>{ anonymousoutOfHoursMessageContent
                ? anonymousoutOfHoursMessageContent
                    .replace(/\[Adult Name\]/gi, location.state?.staffName || '')
                    .replace(/\[From\]/gi, outOfHoursStartTime || '')
                    .replace(/\[To\]/gi, outOfHoursEndTime || '')
                : ''}</div>
            {/* <div style={{ marginTop: 8, fontSize: 13, color: '#856404' }}>
              From {outOfHoursStartTime} to {outOfHoursEndTime}
            </div> */}
          </div>
        )}
        <Container maxWidth="lg">
          <Grid container mt={2}>
            <Grid item lg={12} md={12} sm={12} xs={12} textAlign="center">
              <h1 className="select-staff-heading">{conversationDetails?.title}</h1>
            </Grid>
          </Grid>

          <div className="conversation-container" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',                  
                }}
              >
                <CircularProgress />
                <div style={{ marginTop: '16px', color: '#555', fontSize: '16px' }}>
                  Loading Messages....
                </div>
              </div>
            ) : (
              <Grid container mt={1} rowGap={4}>
                {messages.map((message) => {
                  // Extract attachment information
                  const attachmentURL = message.attachmentURL || message.Attachment?.FileUrl || null;
                  let attachmentFileName = message.attachmentFileName || message.Attachment?.FileName || null;
                  
                  // If no filename is found but URL exists, extract from URL
                  if (!attachmentFileName && attachmentURL) {
                    attachmentFileName = getFilenameFromUrl(attachmentURL);
                  }
                  
                  return (
                    <Grid item lg={12} md={12} sm={12} xs={12} key={message.id}>
                      <div className={message.studentId !== 0 ? 'pupil-msg' : 'staff-msg'}>
                        <div className="msg">
                          {message.messageText}
                          
                          {/* Attachment display */}
                          {attachmentURL && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '12px' }}>
                              {(() => {
                                // Check if it's an image file
                                const isImage = isImageFile(attachmentURL);
                                
                                if (isImage) {
                                  return (
                                    <Avatar 
                                      src={toPupilViewableArtifactUrl(attachmentURL)} 
                                      alt={attachmentFileName || 'Image'}
                                      variant="rounded"
                                      sx={{ 
                                        width: 60, 
                                        height: 60, 
                                        marginLeft: '8px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 0.8,
                                          boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                                        }
                                      }}
                                      onClick={() => handleDownloadAttachment(attachmentURL, attachmentFileName || 'attachment')}
                                    />
                                  );
                                } else {
                                  return (
                                    <Box
                                      onClick={() => handleDownloadAttachment(attachmentURL, attachmentFileName || 'attachment')}
                                      sx={{
                                        marginLeft: '8px',
                                        padding: '10px',
                                        backgroundColor: 'rgba(0,0,0,0.05)',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 60,
                                        height: 60,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: 'rgba(0,0,0,0.08)',
                                          boxShadow: '0 0 5px rgba(0,0,0,0.2)'
                                        }
                                      }}
                                      title="Download attachment"
                                    >
                                      {/* File type icon selection based on extension */}
                                      {(() => {
                                        const ext = getFileExtension(attachmentURL);
                                        
                                        // Select icon based on file extension
                                        if (['pdf'].includes(ext)) {
                                          return <PictureAsPdfIcon sx={{ fontSize: 30, color: '#D93C65' }} />;
                                        } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
                                          return <TableChartIcon sx={{ fontSize: 30, color: '#0F9D58' }} />;
                                        } else if (['doc', 'docx', 'rtf'].includes(ext)) {
                                          return <DescriptionIcon sx={{ fontSize: 30, color: '#4285F4' }} />;
                                        } else if (['mp3', 'wav', 'ogg'].includes(ext)) {
                                          return <AudioFileIcon sx={{ fontSize: 30, color: '#AA46BC' }} />;
                                        } else if (['mp4', 'avi', 'mov', 'wmv'].includes(ext)) {
                                          return <VideoFileIcon sx={{ fontSize: 30, color: '#FF7043' }} />;
                                        } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
                                          return <FolderZipIcon sx={{ fontSize: 30, color: '#795548' }} />;
                                        } else if (['js', 'html', 'css', 'php', 'py', 'java', 'ts', 'jsx', 'tsx'].includes(ext)) {
                                          return <CodeIcon sx={{ fontSize: 30, color: '#607D8B' }} />;
                                        } else if (['txt', 'md'].includes(ext)) {
                                          return <TextSnippetIcon sx={{ fontSize: 30, color: '#607D8B' }} />;
                                        } else {
                                          return <InsertDriveFileIcon sx={{ fontSize: 30, color: '#5F6368' }} />;
                                        }
                                      })()}
                                      <Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 500 }}>
                                        {getFileExtension(attachmentURL).toUpperCase() || 'FILE'}
                                      </Typography>
                                    </Box>
                                  );
                                }
                              })()}
                              <Typography variant="caption" sx={{ fontSize: '9px', mt: 0.5, textAlign: 'center' }}>
                                {getShortFilename(attachmentFileName) || 'Attachment'}
                              </Typography>
                            </div>
                          )}
                        </div>
                        <div className="date">
                          {`${messageSenderLabel(message)}. ${formatDate(message.createdDate)}`}
                        </div>
                      </div>
                    </Grid>
                  );
                })}
                <div ref={messageEndRef} /> {/* Invisible element for scrolling */}
              </Grid>
            )}
            {/* Highlighter div for auto response message */}
            {messages.length > 0 && messages[messages.length - 1].studentId !== 0 && (
              autoResponceMessageContent && autoResponceMessageContent.trim() !== '' ? (
                <div
                  style={{
                    background: '#E3F2FD',
                    color: '#1565C0',
                    border: '1px solid #90CAF9',
                    borderRadius: 8,
                    padding: '16px 24px',
                    marginBottom: 24,
                    fontWeight: 500,
                    fontSize: 16,
                    textAlign: 'left',
                    marginTop: 25,
                  }}
                >
                  {autoResponceMessageContent.replace(/\[Adult Name\]/gi, location.state?.staffName || '')}
                </div>
              ) : (
                <div
                  style={{
                    background: '#E3F2FD',
                    color: '#1565C0',
                    border: '1px solid #90CAF9',
                    borderRadius: 8,
                    padding: '16px 24px',
                    marginBottom: 24,
                    fontWeight: 500,
                    fontSize: 16,
                    textAlign: 'center',
                    marginTop: 25,
                  }}
                >
                  {`${location.state?.staffName || ''} has been sent your message and will get back to you soon.`}
                </div>
              )
            )}
          </div>
          <div className="conversation-editor" style={{ opacity: isConversationClosed ? 0.6 : 1, pointerEvents: isConversationClosed ? 'none' : 'auto' }}>
            {isConversationClosed && (
              <div style={{
                width: '95%',
                padding: '8px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
                textAlign: 'center',                
                fontWeight: 'bold',
                marginBottom: '10px'
              }}>
                This conversation has been closed. You cannot send any more messages.
              </div>
            )}
            <div className="conversation-textfield">
              <TextField
                className="conversation-textarea"
                placeholder={isConversationClosed ? "Conversation closed" : "Type your message here..."}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                disabled={isConversationClosed}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isConversationClosed) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              {/* Hidden file input.
                  Video was absent from the accept list, so the picker filtered it out and a pupil
                  could not attach one at all — reported as "video messages are not sent from the
                  student side". */}
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept="image/*,video/mp4,video/quicktime,video/webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                disabled={isConversationClosed}
              />
              
              {/* File preview UI */}
              {selectedFile && (
                <Box sx={{ 
                  mt: 2, 
                  mb: 2,
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 1, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '4px',
                  maxWidth: '100%'
                }}>
                  {/* Preview for image files */}
                  {selectedFile.type.startsWith('image/') && (
                    <Box sx={{ width: '50px', height: '50px', mr: 1, overflow: 'hidden' }}>
                      <img 
                        src={filePreviewUrl} 
                        alt="File preview" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                  
                  {/* Icon for non-image files */}
                  {!selectedFile.type.startsWith('image/') && (
                    <Box sx={{ width: '50px', height: '50px', mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <InsertDriveFileIcon sx={{ fontSize: 32 }} />
                    </Box>
                  )}
                  
                  {/* File name and size */}
                  <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Typography variant="body2" noWrap>{truncatedFileName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {Math.round(selectedFile.size / 1024)} KB
                    </Typography>
                  </Box>
                  
                  {/* Remove button */}
                  <IconButton size="small" onClick={removeSelectedFile}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              
              <Stack direction="row" columnGap={1.5} alignItems="center">
                <Stack direction="row" columnGap={0.8} alignItems="center">
                  <IconButton 
                    onClick={triggerFileInput}
                    edge="end"
                    disabled={sendingMessage || isConversationClosed}
                  >
                    <AttachFileIcon />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setShowEmojiPicker(true);
                    }}
                    disabled={isConversationClosed}
                  >
                    <MoodOutlinedIcon />
                  </IconButton>
                </Stack>
                <WbButton
                  className="send-btn"
                  CustomButtonText={sendingMessage ? '' : isConversationClosed ? 'Closed' : 'Send'}
                  onClick={handleSendMessage}
                  disabled={(!messageText.trim() && !selectedFile) || sendingMessage || isConversationClosed}
                  startIcon={sendingMessage && <CircularProgress size={27} color="inherit" />}
                />
              </Stack>
              <Popover
                open={showEmojiPicker}
                anchorEl={anchorEl}
                onClose={() => {
                  setAnchorEl(null);
                  setShowEmojiPicker(false);
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <div style={{ padding: '10px' }}>
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      setMessageText((prev) => prev + emojiData.emoji);
                      setShowEmojiPicker(false);
                      setAnchorEl(null);
                    }}
                    width={300}
                    height={400}
                  />
                </div>
              </Popover>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}
